import { JSX } from 'react';
import {
  LocallyDerivedTransformation,
  LocallyDerivedTransformationOperation,
} from '@ardoq/api-types';
import { Stack } from '@ardoq/layout';
import { I18nLocale } from '@ardoq/i18n';
import { DictionaryLookupDetailsSection } from './DictionaryLookupDetailsSection';
import { MathDetailsSection } from './MathDetailsSection';
import { OperationSection } from './OperationSection';
import { ConcatDetailsSection } from './ConcatDetailsSection';
import { BaseTransformationEditorProps, EditorSectionProps } from './types';

/**
 * We only support editing a single transformation for now
 */
export type TransformationsEditorProps = BaseTransformationEditorProps<
  LocallyDerivedTransformation[]
> & {
  i18nLocale?: I18nLocale;
};

export const TransformationsEditor = ({
  type,
  value,
  onValueChange,
  reactiveFieldOptions,
  options,
  i18nLocale,
}: TransformationsEditorProps) => {
  return (
    <TransformationEditor
      type={type}
      value={value?.[0]}
      onValueChange={nextValue => onValueChange(nextValue ? [nextValue] : [])}
      reactiveFieldOptions={reactiveFieldOptions}
      options={options}
      i18nLocale={i18nLocale}
    />
  );
};

export type TransformationEditorProps = BaseTransformationEditorProps<
  LocallyDerivedTransformation | undefined
> & {
  i18nLocale?: I18nLocale;
};

export const TransformationEditor = ({
  type,
  value,
  onValueChange,
  reactiveFieldOptions,
  options,
  i18nLocale,
}: TransformationEditorProps) => {
  return (
    <Stack gap="medium">
      <OperationSection
        type={type}
        value={value}
        onValueChange={onValueChange}
        i18nLocale={i18nLocale}
      />

      {value && (
        <DetailsSection
          type={type}
          value={value}
          onValueChange={onValueChange}
          reactiveFieldOptions={reactiveFieldOptions}
          options={options}
          i18nLocale={i18nLocale}
        />
      )}
    </Stack>
  );
};

type DetailsSectionProps = EditorSectionProps & {
  i18nLocale?: I18nLocale;
};

export const DetailsSection = ({
  type,
  value,
  onValueChange,
  reactiveFieldOptions,
  options,
  i18nLocale,
}: DetailsSectionProps): JSX.Element => {
  switch (value.operation) {
    case LocallyDerivedTransformationOperation.MATH:
      return (
        <MathDetailsSection
          type={type}
          value={value}
          onValueChange={onValueChange}
          reactiveFieldOptions={reactiveFieldOptions}
          i18nLocale={i18nLocale}
        />
      );
    case LocallyDerivedTransformationOperation.DICTIONARY_LOOKUP:
      return (
        <DictionaryLookupDetailsSection
          type={type}
          value={value}
          onValueChange={onValueChange}
          reactiveFieldOptions={reactiveFieldOptions}
          options={options}
          i18nLocale={i18nLocale}
        />
      );
    case LocallyDerivedTransformationOperation.CONCAT:
      return (
        <ConcatDetailsSection
          type={type}
          value={value}
          onValueChange={onValueChange}
          reactiveFieldOptions={reactiveFieldOptions}
          i18nLocale={i18nLocale}
        />
      );
  }
};
